import {
  fetchProjectById,
  fetchProjectBySharingId,
  fetchProjects,
  fetchProjectsAndTasks,
} from '@api/projects.js';
import _State from '@states/_State.js';

const initialState = {
  folders: [],
  tasks: [],
  rootFolderId: null,
  isLoading: true,
  subscriptionError: null,
  rootFolderOrganizationId: null,
  sortColumn: 'createdAt',
  sortDirection: 'asc',
  hasBeenLoaded: false,
};

const ProjectsState = _State('projects', 'setProjects', initialState);
export const LoadedFolderIds = _State(
  'loadedFolderIds',
  'setLoadedFolderIds',
  [],
);

ProjectsState.loadProjects = async (organizationId = null) => {
  const currentState = ProjectsState.projects;
  try {
    const projectResponse = await fetchProjects(organizationId);
    if (projectResponse.status !== 'success') {
      throw new Error('Failed to load projects');
    }
    // I just renamed folderInfo to rootProjectInfo for clarity (I didnt change the actual API response in case it breaks things elsewhere)
    const { folderInfo: rootProjectInfo, subfolders } =
      projectResponse.response;

    // Merge the folder into the folders array if it doesn't exist
    const existingFolders = currentState.folders || [];
    const existingById = new Map(existingFolders.map(f => [f.id, f]));

    const mergedFolders = subfolders.map(
      ({ folderName, parentFolderId, creatorId, isDefault, ...rest }) => {
        const existing = existingById.get(rest.id);

        // No previous folder → just take everything from restfolder
        if (!existing)
          return {
            ...rest,
            name: folderName,
            parentId: parentFolderId,
            uid: creatorId,
            isDefault: isDefault || false,
          };

        // FOLDER EXISTS → KEEP missing fields from existing
        return {
          ...rest,
          name: folderName,
          parentId: parentFolderId,
          uid: creatorId,
          isDefault: isDefault || false,
          columnValues: existing.columnValues,
          customColumns: existing.customColumns,
        };
      },
    );

    ProjectsState.setProjects({
      rootFolderId: rootProjectInfo.id,
      rootFolderInfo: {
        ...rootProjectInfo,
        uid: rootProjectInfo.creatorId,
      },
      folders: mergedFolders,
      tasks: [...currentState.tasks],
      isLoading: false,
      subscriptionError: null,
      rootFolderOrganizationId: rootProjectInfo.organizationId,
      hasBeenLoaded: true,
    });

    return {
      success: true,
      folders: mergedFolders,
      project: rootProjectInfo,
      tasks: [],
      isOwner: true,
    };
  } catch (error) {
    console.error('Error loading projects:', error);
    const subscriptionError = error?.error?.code ?? null;

    ProjectsState.setProjects({
      ...initialState,
      isLoading: false,
      subscriptionError,
    });
  }
};

ProjectsState.getProjectByShareId = async sharedProjectId => {
  try {
    const currentState = ProjectsState.projects;
    // Try to find the project and its tasks in the current state first
    const existingProjectFolder = currentState.folders?.find(
      folder => folder.shortSharingId === sharedProjectId,
    );
    if (existingProjectFolder) {
      const existingTasks = currentState.tasks.filter(
        task => task.parentId === existingProjectFolder.id,
      );

      return {
        success: true,
        project: existingProjectFolder,
        tasks: existingTasks,
      };
    }

    // If not in state or tasks are missing/stale, fetch from API (which could be our mock)
    const projectResponse = await fetchProjectBySharingId(sharedProjectId);

    if (projectResponse.status !== 'success') {
      throw new Error(
        projectResponse.error || 'Project not found after API call',
      );
    }

    const { folderInfo, subfolders, isOwner } = projectResponse.response;

    const rootTasks = folderInfo.cadfiles.map(cadfile => ({
      id: cadfile.cadFileID,
      name: cadfile.name || 'Untitled',
      createdAt: cadfile.createdAt,
      updatedAt: cadfile.updatedAt,
      thumbnail: cadfile.thumbnail,
      columnValues: cadfile.columnValues,
      customColumns: cadfile.customColumns,
      parentId: folderInfo.id,
      accessType: cadfile.accessType,
      sharingId: cadfile.sharingId,
      uid: cadfile.uid,
      organizationId: folderInfo.organizationId,
      commentsEnabled: cadfile.commentsEnabled,
      cadfileAccessLevel: cadfile.cadfileAccessLevel,
      reconstructions: cadfile.reconstructions,
      rowOrder: cadfile.rowOrder,
    }));

    const folders = [];
    const subfolderTasks = [];
    subfolders?.forEach(subfolder => {
      folders.push({
        id: subfolder.id,
        name: subfolder.folderName,
        createdAt: subfolder.createdAt,
        updatedAt: subfolder.updatedAt,
        metadata: subfolder.metadata,
        parentId: subfolder.parentFolderId,
        accessType: subfolder.accessType,
        sharingId: subfolder.sharingId,
        shortSharingId: subfolder.shortSharingId,
        columnValues: subfolder.columnValues,
        customColumns: subfolder.customColumns,
        uid: subfolder.creatorId,
        organizationId: subfolder.organizationId,
        isPublic: subfolder.isPublic,
        isShared: subfolder.isShared,
        rowOrder: subfolder.rowOrder,
        isDefault: subfolder.isDefault || false,
      });

      subfolderTasks?.push(
        ...subfolder?.cadfiles?.map(cadfile => ({
          id: cadfile.cadFileID,
          name: cadfile.name || 'Untitled',
          createdAt: cadfile.createdAt,
          updatedAt: cadfile.updatedAt,
          thumbnail: cadfile.thumbnail,
          parentId: subfolder.id,
          parentFolderName: subfolder.folderName,
          accessType: cadfile.accessType,
          sharingId: cadfile.sharingId,
          columnValues: cadfile.columnValues,
          uid: cadfile.uid,
          organizationId: subfolder.organizationId,
          cadfileAccessLevel: cadfile.cadfileAccessLevel,
          commentsEnabled: cadfile.commentsEnabled,
          isPublic: cadfile.isPublic,
          isShared: cadfile.isShared,
          reconstructions: cadfile.reconstructions,
          rowOrder: cadfile.rowOrder,
        })),
      );
    });

    const allTasks = [...rootTasks, ...subfolderTasks];

    ProjectsState.setProjects({
      // Small workaround for inconsistent API responses:
      // when the user is not the owner, folderInfo is returned in a different form
      folders: !isOwner ? [folderInfo] : folders,
      tasks: allTasks,
      rootFolderId: folderInfo.id,
      rootFolderInfo: {
        ...folderInfo,
        uid: folderInfo.creatorId,
      },
      isLoading: false,
      subscriptionError: null,
      rootFolderOrganizationId: folderInfo.organizationId,
      hasBeenLoaded: true,
    });

    // Track which folders have their tasks loaded
    // For shared projects, all tasks are loaded upfront
    const loadedFolderIds = [folderInfo.id, ...folders.map(f => f.id)];

    LoadedFolderIds.setLoadedFolderIds(loadedFolderIds);

    return {
      success: true,
      folders: folders,
      project: folderInfo,
      tasks: allTasks,
      isOwner: isOwner,
    };
  } catch (error) {
    console.error(
      'Critical error in ProjectsState.getProjectByShareId:',
      error,
    );
    return { success: false, error };
  }
};

ProjectsState.getProjectById = async projectId => {
  try {
    const currentState = ProjectsState.projects;
    // Try to find the project in the current state first
    const existingProjectFolder = currentState.folders?.find(
      folder => folder.id === projectId,
    );

    // Check if this folder's tasks have already been loaded
    const tasksAlreadyLoaded =
      LoadedFolderIds.loadedFolderIds.includes(projectId) || false;

    if (existingProjectFolder && tasksAlreadyLoaded) {
      // Tasks were already loaded, return from cache
      const existingTasks = currentState.tasks.filter(
        task => task.parentId === projectId,
      );

      return {
        success: true,
        project: existingProjectFolder,
        tasks: existingTasks,
        fromCache: true,
      };
    }

    // If folder exists but tasks not loaded, or folder doesn't exist, fetch from API
    const projectResponse = await fetchProjectById(projectId);

    if (projectResponse.status !== 'success') {
      throw new Error(
        projectResponse.error || 'Project not found after API call',
      );
    }

    const { project, tasks } = projectResponse.response;

    const tasksArray = [];
    tasks?.forEach(cadfile => {
      tasksArray.push({
        id: cadfile.cadFileID,
        name: cadfile.name || 'Untitled',
        createdAt: cadfile.createdAt,
        updatedAt: cadfile.updatedAt,
        thumbnail: cadfile.thumbnail,
        parentId: project.id,
        parentFolderName: project.folderName,
        accessType: cadfile.accessType,
        sharingId: cadfile.sharingId,
        columnValues: cadfile.columnValues,
        uid: cadfile.uid,
        organizationId: project.organizationId,
        cadfileAccessLevel: cadfile.cadfileAccessLevel,
        commentsEnabled: cadfile.commentsEnabled,
        isPublic: cadfile.isPublic,
        isShared: cadfile.isShared,
        reconstructions: cadfile.reconstructions,
        rowOrder: cadfile.rowOrder,
      });
    });

    // Merge new tasks with existing tasks (remove old tasks for this folder first)
    const existingTasksFromOtherFolders = currentState.tasks.filter(
      task => task.parentId !== projectId,
    );
    const mergedTasks = [...existingTasksFromOtherFolders, ...tasksArray];

    // Mark this folder as having its tasks loaded
    const currentLoadedIds = LoadedFolderIds.loadedFolderIds || [];
    const updatedLoadedIds = currentLoadedIds.includes(projectId)
      ? currentLoadedIds
      : [...currentLoadedIds, projectId];

    // Parse project to match folder structure in state
    const projectParsed = {
      ...project,
      name: project.folderName,
      parentId: project.parentFolderId,
      uid: project.creatorId,
      isDefault: project.isDefault || false,
    };

    // Merge the folder into the folders array if it doesn't exist
    const existingFolders = currentState.folders || [];
    const folderExists = existingFolders.some(f => f.id === projectId);
    const mergedFolders = folderExists
      ? existingFolders.map(f => (f.id === projectId ? projectParsed : f))
      : [...existingFolders, projectParsed];

    ProjectsState.setProjects({
      ...currentState,
      folders: mergedFolders,
      tasks: mergedTasks,
      rootFolderId: currentState.rootFolderId,
      isLoading: false,
      subscriptionError: null,
      rootFolderOrganizationId: project.organizationId,
      // hasBeenLoaded: true, -> do not set this here to avoid overwriting the flag when loading a single project
    });
    LoadedFolderIds.setLoadedFolderIds(updatedLoadedIds);

    return {
      success: true,
      folders: mergedFolders,
      project: projectParsed,
      tasks: tasksArray,
      isOwner: true,
      fromCache: false,
    };
  } catch (error) {
    console.error('Critical error in ProjectsState.getProjectById:', error);
    return { success: false, error };
  }
};

ProjectsState.getProjectsAndTasks = async (organizationId = null) => {
  try {
    const projectResponse = await fetchProjectsAndTasks(organizationId);
    if (projectResponse.status !== 'success') {
      throw new Error('Failed to load projects');
    }
    const { folderInfo, subfolders, isOwner } = projectResponse.response;

    const rootTasks = folderInfo.cadfiles.map(cadfile => ({
      id: cadfile.cadFileID,
      name: cadfile.name || 'Untitled',
      createdAt: cadfile.createdAt,
      updatedAt: cadfile.updatedAt,
      thumbnail: cadfile.thumbnail,
      columnValues: cadfile.columnValues,
      customColumns: cadfile.customColumns,
      parentId: folderInfo.id,
      accessType: cadfile.accessType,
      sharingId: cadfile.sharingId,
      uid: cadfile.uid,
      organizationId: folderInfo.organizationId,
      commentsEnabled: cadfile.commentsEnabled,
      cadfileAccessLevel: cadfile.cadfileAccessLevel,
      reconstructions: cadfile.reconstructions,
      rowOrder: cadfile.rowOrder,
    }));

    const folders = [];
    const subfolderTasks = [];
    subfolders?.forEach(subfolder => {
      folders.push({
        id: subfolder.id,
        name: subfolder.folderName,
        createdAt: subfolder.createdAt,
        updatedAt: subfolder.updatedAt,
        metadata: subfolder.metadata,
        parentId: subfolder.parentFolderId,
        accessType: subfolder.accessType,
        sharingId: subfolder.sharingId,
        shortSharingId: subfolder.shortSharingId,
        columnValues: subfolder.columnValues,
        customColumns: subfolder.customColumns,
        uid: subfolder.creatorId,
        organizationId: subfolder.organizationId,
        isPublic: subfolder.isPublic,
        isShared: subfolder.isShared,
        rowOrder: subfolder.rowOrder,
        isDefault: subfolder.isDefault || false,
      });

      subfolderTasks?.push(
        ...subfolder?.cadfiles?.map(cadfile => ({
          id: cadfile.cadFileID,
          name: cadfile.name || 'Untitled',
          createdAt: cadfile.createdAt,
          updatedAt: cadfile.updatedAt,
          thumbnail: cadfile.thumbnail,
          parentId: subfolder.id,
          parentFolderName: subfolder.folderName,
          accessType: cadfile.accessType,
          sharingId: cadfile.sharingId,
          columnValues: cadfile.columnValues,
          uid: cadfile.uid,
          organizationId: subfolder.organizationId,
          cadfileAccessLevel: cadfile.cadfileAccessLevel,
          commentsEnabled: cadfile.commentsEnabled,
          isPublic: cadfile.isPublic,
          isShared: cadfile.isShared,
          reconstructions: cadfile.reconstructions,
          rowOrder: cadfile.rowOrder,
        })),
      );
    });

    const allTasks = [...rootTasks, ...subfolderTasks];

    ProjectsState.setProjects({
      folders: folders,
      tasks: allTasks,
      rootFolderId: folderInfo.id,
      rootFolderInfo: {
        ...folderInfo,
        uid: folderInfo.creatorId,
      },
      isLoading: false,
      subscriptionError: null,
      rootFolderOrganizationId: folderInfo.organizationId,
      hasBeenLoaded: true,
    });

    LoadedFolderIds.setLoadedFolderIds([
      folderInfo.id,
      ...folders.map(f => f.id),
    ]);

    return {
      success: true,
      folders: folders,
      project: folderInfo,
      tasks: allTasks,
      isOwner: isOwner,
    };
  } catch (error) {
    console.error('Error loading projects:', error);
    const subscriptionError = error?.error?.code ?? null;

    ProjectsState.setProjects({
      ...initialState,
      isLoading: false,
      subscriptionError,
    });
  }
};

/**
 * Targeted refresh for projects whose tasks were modified by the AI.
 * Evicts those projects from the loaded-folder cache so getProjectById
 * performs a fresh API call instead of returning stale data.
 */
ProjectsState.refreshProjectsByTaskIds = async taskIds => {
  const currentState = ProjectsState.projects;
  const stateTasks = currentState.tasks || [];

  // Resolve unique parent project IDs from the provided task IDs
  const affectedProjectIds = [
    ...new Set(
      taskIds
        .map(taskId => stateTasks.find(t => t.id === taskId)?.parentId)
        .filter(Boolean),
    ),
  ];

  if (affectedProjectIds.length === 0) return;

  // Evict affected projects from the loaded-folder cache
  const currentLoadedIds = LoadedFolderIds.loadedFolderIds || [];
  LoadedFolderIds.setLoadedFolderIds(
    currentLoadedIds.filter(id => !affectedProjectIds.includes(id)),
  );

  // Re-fetch each affected project in parallel (getProjectById will now hit the API)
  await Promise.all(
    affectedProjectIds.map(id => ProjectsState.getProjectById(id)),
  );
};

export default ProjectsState;

export const CurrentItemsState = _State('currentItems', 'setCurrentItems', []);

// use to track which folder the user navigated to: null = rootFolder / number = projectId
export const CurrentFolderId = _State(
  'currentFolderId',
  'setCurrentFolderId',
  null,
);

// State to the current selected items for the AI Analyzer
export const AiSelectedItems = _State('selectedItems', 'setSelectedItems', {
  projects: [], // Array of project IDs
  tasks: [], // Array of task IDs
});
